import RichText from '@quno/patient-journey/src/components/ui/ContentBlocks/RichText';
import { useTemplateWords } from '@quno/patient-journey/src/hooks/useTemplateWords';
import clsx from 'clsx';
import { Card as QdsCard } from '@quno/patient-journey/src/components/Card/Card';
import { useEffect, useState } from 'react';
import { getRichTextMultilocale } from '@quno/onboarding/src/generator/configs/componentMapper';
import styles from './Card.module.scss';
import type { Document } from '@contentful/rich-text-types';
import type { FormLocales } from '@quno/onboarding/src/constants/locales';
import type { StringAttribute } from '@quno/onboarding/src/generator/types/generics/ModelAttributes';
import type { CardProps as QdsCardProps } from '@quno/patient-journey/src/components/Card/card.config';

export type CardProps = {
  image?: JSX.Element | null;
  title?: string | null;
  footer?: string | null;
  text: StringAttribute | null;
  locale: FormLocales;
};

export const Card = ({
  text,
  image,
  title,
  footer,
  backgroundColorOverwrite,
  darkModeEnabled,
  imageStyle,
  imagePosition,
  cardBorderRadius,
  fontColorOverride,
  locale,
}: Omit<QdsCardProps, 'text'> & CardProps): JSX.Element => {
  const { replaceTemplateWords } = useTemplateWords();
  const [textContent, setTextContent] = useState<Document | undefined>();

  useEffect(() => {
    const run = async (): Promise<void> => {
      const content = await getRichTextMultilocale(text, locale);
      setTextContent(content);
    };

    void run();
  }, [text, locale]);

  const alignSelfMapper = (position: 'left' | 'center'): string => {
    switch (position) {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      default:
        return 'flex-start';
    }
  };

  return (
    <QdsCard
      className={clsx(styles.item, styles[(imageStyle as string) || 'circle'])}
      footer={footer}
      backgroundColorOverwrite={backgroundColorOverwrite}
      darkModeEnabled={darkModeEnabled}
      style={{ borderRadius: cardBorderRadius || 0 }}
      fontColorOverride={fontColorOverride}
    >
      <div
        className={styles.imageWrapper}
        style={{
          alignSelf: alignSelfMapper(imagePosition as 'left' | 'center'),
        }}
      >
        {image}
      </div>
      <div className={styles.text}>
        <h4
          className={styles.title}
          style={{
            color: fontColorOverride,
          }}
        >
          {replaceTemplateWords(title!)}
        </h4>
        <div>
          <RichText fields={textContent} noWrapper />
        </div>
      </div>
    </QdsCard>
  );
};
